import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toGenexusParams } from '../../../shared/api/genexus-query';
import type { DataTableQuery } from '../../../shared/components/DataTable';
import type { CompanyNotasFiscaisListExtraParams } from './types';

export type StreamStatus = 'live' | 'polling';

/** Cadência do polling de fallback (casa com o SSE_POLL_INTERVAL_MS do BFF). */
const POLL_INTERVAL_MS = 15000;

/**
 * Auto-refresh da lista de NF Empresa. Caminho preferido: SSE
 * (`GET .../notas-fiscais/stream`) — o BFF faz poll server-side com os MESMOS
 * filtros do grid e empurra `changed`; aqui invalidamos a query → react-query
 * refaz o fetch. Ver `registerCompanyNotaFiscalStream` no BFF.
 *
 * Fallback: atrás do IIS + HttpPlatformHandler o `text/event-stream` é
 * bufferizado (a resposta não flui, o `EventSource` nunca abre — fica "pending"
 * com 0 bytes). Nesse caso caímos para **polling** client-side (invalida a
 * query a cada `POLL_INTERVAL_MS`), então a lista atualiza sozinha de qualquer
 * jeito. Quando o SSE está `live`, o polling fica desligado (o push basta).
 *
 * Retorna `'live'` (SSE ativo) ou `'polling'` (fallback) — em ambos os casos há
 * atualização automática; nunca fica "parado".
 */
export function useCompanyNotasFiscaisStream(
  companyId: number | null,
  query: DataTableQuery,
  extra: CompanyNotasFiscaisListExtraParams,
  enabled = true,
): StreamStatus {
  const qc = useQueryClient();
  const [live, setLive] = useState(false);

  // String estável dos params (value-compared no dep do efeito), evita reconectar
  // a cada render mesmo com `extra` recriado inline no caller.
  const paramsStr = new URLSearchParams(
    toGenexusParams(query, extra as Record<string, string | number | boolean | undefined | null>),
  ).toString();

  const listKey = useMemo(
    () =>
      companyId === null
        ? (['companies', 'all', 'notas-fiscais'] as const)
        : (['companies', companyId, 'notas-fiscais'] as const),
    [companyId],
  );

  useEffect(() => {
    // Lista de todas as empresas não tem stream. `EventSource` ausente (jsdom
    // nos testes, ou browser muito antigo) → só polling.
    if (!enabled || companyId === null || typeof EventSource === 'undefined') return;
    const es = new EventSource(`/api/v1/companies/${companyId}/notas-fiscais/stream?${paramsStr}`, {
      withCredentials: true,
    });
    const onChanged = (): void => {
      qc.invalidateQueries({ queryKey: ['companies', companyId, 'notas-fiscais'] });
    };
    // Sincroniza estado externo (SSE) no React — uso legítimo de efeito.
     
    es.addEventListener('connected', () => setLive(true));
    es.addEventListener('changed', onChanged);
    es.addEventListener('unauthorized', () => {
      es.close();
      setLive(false);
    });
    es.onopen = (): void => setLive(true);
    // Erro/queda: sai do modo live → o efeito de polling assume. O EventSource
    // tenta reconectar sozinho; se voltar, o `onopen`/`connected` religa o live.
    es.onerror = (): void => setLive(false);

    return () => {
      es.close();
      setLive(false);
    };
  }, [companyId, paramsStr, enabled, qc]);

  // Fallback: enquanto NÃO estiver `live`, atualiza por polling. Desliga assim
  // que o SSE abrir (evita fetch duplicado com o push).
  useEffect(() => {
    if (!enabled || live) return;
    const id = setInterval(() => {
      qc.invalidateQueries({ queryKey: listKey });
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [live, enabled, qc, listKey]);

  return live ? 'live' : 'polling';
}
